/**
 * AdminClientsPage - lista e detalhe de clientes
 * Melhorias: corrigido IDs dos planos, adicionada exportação CSV, data de registo, badge de trial
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Eye, Crown, Loader2, Download, Users } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import { fmtDateTime } from "@/lib/date";
import { toast } from "sonner";

const PLAN_LABELS = {
    economico: "Económico",
    profissional: "Profissional",
    empresarial: "Empresarial",
    agencia: "Agência",
};

const SUB_STATUS_MAP = {
    ativa: "aprovado",
    trial: "pendente",
    expirada: "rejeitado",
    cancelada: "rejeitado",
};

export default function AdminClientsPage() {
    const [clients, setClients] = useState([]);
    const [search, setSearch] = useState("");
    const [sel, setSel] = useState(null);
    const [detail, setDetail] = useState(null);
    const [activatePlan, setActivatePlan] = useState("profissional");
    const [activateDays, setActivateDays] = useState(30);
    const [activating, setActivating] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/clients");
            setClients(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = clients.filter(
        (c) => !search ||
            c.name?.toLowerCase().includes(search.toLowerCase()) ||
            c.email?.toLowerCase().includes(search.toLowerCase()) ||
            c.company?.toLowerCase().includes(search.toLowerCase())
    );

    const openDetail = async (client) => {
        setSel(client);
        const { data } = await api.get(`/admin/clients/${client.id}`);
        setDetail(data);
    };

    const activate = async () => {
        if (!sel) return;
        setActivating(true);
        try {
            await api.post(`/admin/clients/${sel.id}/activate?plan_id=${activatePlan}&days=${activateDays}`);
            toast.success(`Subscrição ativada (${PLAN_LABELS[activatePlan] || activatePlan})`);
            await load();
            setSel(null);
            setDetail(null);
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setActivating(false);
        }
    };

    const exportCsv = () => {
        const rows = [
            ["Nome", "Email", "Empresa", "País", "Plano", "Estado", "Data de Registo"],
            ...clients.map((c) => [
                c.name || "",
                c.email || "",
                c.company || "",
                c.country || "",
                c.plan_id ? (PLAN_LABELS[c.plan_id] || c.plan_id) : "—",
                c.subscription_status || "",
                c.created_at ? new Date(c.created_at).toLocaleDateString("pt-PT") : "",
            ]),
        ];
        const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mesafa-clientes-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exportado com sucesso");
    };

    return (
        <div className="space-y-6" data-testid="admin-clients-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Clientes</h1>
                    <p className="text-muted-foreground">Gerir todos os clientes da plataforma.</p>
                </div>
                <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="admin-clients-export-csv">
                    <Download className="h-4 w-4" /> Exportar CSV
                </Button>
            </div>

            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Pesquisar nome, email, empresa..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="admin-clients-search"
                    />
                </div>
                <Badge variant="outline" className="gap-1 hidden sm:inline-flex">
                    <Users className="h-3 w-3" /> {filtered.length} cliente{filtered.length !== 1 ? "s" : ""}
                </Badge>
            </div>

            <Card className="glass-card p-0 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40 text-xs uppercase">
                            <tr>
                                <th className="text-left p-3">Cliente</th>
                                <th className="text-left p-3 hidden md:table-cell">Empresa</th>
                                <th className="text-left p-3 hidden lg:table-cell">Plano</th>
                                <th className="text-left p-3">Estado</th>
                                <th className="text-left p-3 hidden lg:table-cell">Registo</th>
                                <th className="text-right p-3">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="border-t border-white/5">
                                        <td className="p-3"><div className="h-4 w-32 bg-white/10 rounded animate-pulse" /></td>
                                        <td className="p-3 hidden md:table-cell"><div className="h-4 w-24 bg-white/10 rounded animate-pulse" /></td>
                                        <td className="p-3 hidden lg:table-cell"><div className="h-4 w-20 bg-white/10 rounded animate-pulse" /></td>
                                        <td className="p-3"><div className="h-5 w-16 bg-white/10 rounded animate-pulse" /></td>
                                        <td className="p-3 hidden lg:table-cell"><div className="h-4 w-24 bg-white/10 rounded animate-pulse" /></td>
                                        <td className="p-3 text-right"><div className="h-8 w-8 bg-white/10 rounded ml-auto animate-pulse" /></td>
                                    </tr>
                                ))
                            ) : (
                                <>
                                    {filtered.map((c) => (
                                        <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-client-row-${c.id}`}>
                                            <td className="p-3">
                                                <div className="font-medium">{c.name}</div>
                                                <div className="text-xs text-muted-foreground">{c.email}</div>
                                            </td>
                                            <td className="p-3 hidden md:table-cell">{c.company || "—"}</td>
                                            <td className="p-3 hidden lg:table-cell">
                                                {c.plan_id
                                                    ? <Badge variant="outline" className="border-[#6B21D9]/30 bg-[#6B21D9]/10 text-[#A78BFA]">{PLAN_LABELS[c.plan_id] || c.plan_id}</Badge>
                                                    : <span className="text-muted-foreground text-xs">Sem plano</span>}
                                            </td>
                                            <td className="p-3">
                                                <StatusBadge status={SUB_STATUS_MAP[c.subscription_status] || "rascunho"} />
                                                {c.subscription_status === "trial" && (
                                                    <div className="text-[10px] text-muted-foreground mt-0.5">Trial</div>
                                                )}
                                            </td>
                                            <td className="p-3 hidden lg:table-cell text-xs text-muted-foreground">
                                                {c.created_at ? new Date(c.created_at).toLocaleDateString("pt-PT") : "—"}
                                            </td>
                                            <td className="p-3 text-right">
                                                <Button size="sm" variant="outline" onClick={() => openDetail(c)} data-testid={`admin-client-view-${c.id}`}>
                                                    <Eye className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                        <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Sem resultados.</td></tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={!!sel} onOpenChange={(v) => { if (!v) { setSel(null); setDetail(null); } }}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{sel?.name}</DialogTitle>
                        <DialogDescription>{sel?.email} · {sel?.company || "Sem empresa"}</DialogDescription>
                    </DialogHeader>
                    {!detail ? (
                        <div className="py-10 flex justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <Card className="p-3 rounded-xl bg-secondary/40">
                                    <div className="text-xs text-muted-foreground">Posts</div>
                                    <div className="font-display text-xl font-semibold">{detail.posts_count}</div>
                                </Card>
                                <Card className="p-3 rounded-xl bg-secondary/40">
                                    <div className="text-xs text-muted-foreground">Canais</div>
                                    <div className="font-display text-xl font-semibold">{detail.channels.filter((c) => c.connected).length}</div>
                                </Card>
                                <Card className="p-3 rounded-xl bg-secondary/40">
                                    <div className="text-xs text-muted-foreground">Pagamentos</div>
                                    <div className="font-display text-xl font-semibold">{detail.payments.length}</div>
                                </Card>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <span className="text-xs text-muted-foreground block">País</span>
                                    <span>{detail.user?.country || "—"}</span>
                                </div>
                                <div>
                                    <span className="text-xs text-muted-foreground block">Telefone</span>
                                    <span>{detail.user?.phone || "—"}</span>
                                </div>
                                <div>
                                    <span className="text-xs text-muted-foreground block">Plano atual</span>
                                    <span>{detail.user?.plan_id ? (PLAN_LABELS[detail.user.plan_id] || detail.user.plan_id) : "Sem plano"}</span>
                                </div>
                                <div>
                                    <span className="text-xs text-muted-foreground block">Subscrição expira</span>
                                    <span>{detail.user?.subscription_expires_at ? fmtDateTime(detail.user.subscription_expires_at) : "—"}</span>
                                </div>
                            </div>

                            <Card className="p-4 rounded-xl border-[#6B21D9]/30 bg-[#6B21D9]/5">
                                <h4 className="font-display font-semibold mb-3 flex items-center gap-1">
                                    <Crown className="h-4 w-4 text-[#A78BFA]" /> Ativar/Renovar subscrição
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-muted-foreground">Plano</label>
                                        <Select value={activatePlan} onValueChange={setActivatePlan}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="economico">Económico</SelectItem>
                                                <SelectItem value="profissional">Profissional</SelectItem>
                                                <SelectItem value="empresarial">Empresarial</SelectItem>
                                                <SelectItem value="agencia">Agência</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-xs text-muted-foreground">Duração (dias)</label>
                                        <Input type="number" min={1} value={activateDays} onChange={(e) => setActivateDays(Number(e.target.value))} />
                                    </div>
                                </div>
                                <Button
                                    onClick={activate}
                                    disabled={activating}
                                    className="mt-3 btn-grad-purple text-white w-full"
                                    data-testid="admin-activate-subscription"
                                >
                                    {activating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Crown className="h-4 w-4 mr-2" />}
                                    {activating ? "A ativar..." : "Ativar subscrição"}
                                </Button>
                            </Card>

                            <div>
                                <h4 className="font-display font-semibold mb-2">Histórico de pagamentos</h4>
                                {detail.payments.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">Sem pagamentos registados.</p>
                                ) : (
                                    <ul className="divide-y divide-white/5">
                                        {detail.payments.map((p) => (
                                            <li key={p.id} className="py-2 flex items-center justify-between text-sm">
                                                <div>
                                                    <div>{PLAN_LABELS[p.plan_id] || p.plan_id} · {fmtDateTime(p.created_at)}</div>
                                                    <div className="text-xs text-muted-foreground">{p.method === "multicaixa_express" ? "Multicaixa Express" : p.method === "e_kwanza" ? "É-Kwanza" : "Transferência"}</div>
                                                </div>
                                                <div className="flex gap-2 items-center">
                                                    <span>{p.currency === "USD" ? `${p.amount_usd} USD` : `${(p.amount_aoa || 0).toLocaleString("pt-PT")} AOA`}</span>
                                                    <StatusBadge status={p.status} />
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setSel(null); setDetail(null); }}>Fechar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
